// src/components/IdleScreen.js
// ─────────────────────────────────────────────────────────────
//  Attract / idle screen
//  Shows after 60s of no interaction
//  Tap anywhere to return to menu
//  Displays vendor branding + rotating promotions
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';

const SLIDES = [
  { emoji: '🌯', headline: 'Fresh Shawarmas', sub: 'Made to order — every time', color: '#f5a623' },
  { emoji: '🍔', headline: 'Juicy Burgers',   sub: 'Double patty, extra cheese',  color: '#e05a5a' },
  { emoji: '🍕', headline: 'Wood-fired Pizza', sub: 'Crispy base, fresh toppings', color: '#4caf7d' },
  { emoji: '🥤', headline: 'Cool Drinks',      sub: 'Mango lassi, fresh juices',   color: '#5a9fe0' },
];

export default function IdleScreen({ vendorName, onWake }) {
  const [slide,     setSlide]     = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [visible,   setVisible]   = useState(false);

  // Fade in on mount
  useEffect(() => {
    setTimeout(() => setVisible(true), 50);
  }, []);

  // Auto-rotate slides
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 3500);
    return () => clearInterval(t);
  }, []);

  // Pulsing "tap to order" countdown
  useEffect(() => {
    const t = setInterval(() => setCountdown((c) => c > 0 ? c - 1 : 3), 1000);
    return () => clearInterval(t);
  }, []);

  const current = SLIDES[slide];

  return (
    <div
      onClick={onWake}
      onTouchStart={onWake}
      style={{
        position:        'fixed',
        inset:           0,
        zIndex:          9999,
        background:      '#0a0a0a',
        display:         'flex',
        flexDirection:   'column',
        alignItems:      'center',
        justifyContent:  'center',
        cursor:          'pointer',
        userSelect:      'none',
        opacity:         visible ? 1 : 0,
        transition:      'opacity .5s ease',
        overflow:        'hidden',
      }}
    >
      {/* Animated background circles */}
      <div style={{
        position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none',
      }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{
            position:     'absolute',
            borderRadius: '50%',
            background:   current.color + '08',
            width:        `${200 + i * 120}px`,
            height:       `${200 + i * 120}px`,
            top:          '50%',
            left:         '50%',
            transform:    'translate(-50%, -50%)',
            animation:    `pulse-ring ${2 + i * 0.5}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
          }} />
        ))}
      </div>

      <style>{`
        @keyframes pulse-ring {
          0%, 100% { transform: translate(-50%,-50%) scale(1); opacity: .6; }
          50%       { transform: translate(-50%,-50%) scale(1.08); opacity: .2; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-16px); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Logo / vendor name */}
      <div style={{
        position: 'absolute', top: 40,
        fontSize: 18, fontWeight: 700,
        color: current.color, letterSpacing: 1,
      }}>
        ⚡ {vendorName || 'QuickServe Kiosk'}
      </div>

      {/* Main slide content */}
      <div key={slide} style={{
        textAlign:  'center',
        animation:  'slide-up .5s ease',
        padding:    '0 40px',
        maxWidth:   600,
      }}>
        <div style={{
          fontSize:  120,
          lineHeight: 1,
          marginBottom: 24,
          animation: 'float 3s ease-in-out infinite',
          display:   'inline-block',
        }}>
          {current.emoji}
        </div>

        <div style={{
          fontSize:    52,
          fontWeight:  800,
          color:       current.color,
          marginBottom: 12,
          lineHeight:  1.1,
        }}>
          {current.headline}
        </div>

        <div style={{
          fontSize:   22,
          color:      '#a09890',
          marginBottom: 48,
        }}>
          {current.sub}
        </div>
      </div>

      {/* Tap to order CTA */}
      <div style={{
        position:     'absolute',
        bottom:       60,
        textAlign:    'center',
      }}>
        <div style={{
          fontSize:     32,
          fontWeight:   700,
          color:        '#fff',
          padding:      '20px 48px',
          background:   current.color,
          borderRadius: 60,
          marginBottom: 16,
          boxShadow:    `0 0 40px ${current.color}66`,
          animation:    'pulse-ring 1.5s ease-in-out infinite',
        }}>
          👆 TAP TO ORDER
        </div>

        {/* Slide dots */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          {SLIDES.map((_, i) => (
            <div key={i} style={{
              width:        i === slide ? 24 : 8,
              height:       8,
              borderRadius: 4,
              background:   i === slide ? current.color : '#333',
              transition:   'all .3s ease',
            }} />
          ))}
        </div>
      </div>

      {/* Clock */}
      <div style={{
        position:   'absolute',
        top:        40,
        right:      40,
        fontSize:   16,
        color:      '#444',
        fontFamily: 'monospace',
      }}>
        {new Date().toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}
